/**
 * Utility function to extract error messages from backend API responses
 * @param error - The error object from the API call
 * @returns A formatted error message from the backend or a fallback message
 */
export const getBackendErrorMessage = (error: any): string => {
  // Check for backend error message in different possible locations
  if (error?.response?.data?.errors?.[0]?.message) {
    return error.response.data.errors[0].message;
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  // Check for multiple errors and combine them
  if (
    error?.response?.data?.errors &&
    Array.isArray(error.response.data.errors)
  ) {
    const messages = error.response.data.errors
      .map((err: any) => err.message || err)
      .filter(Boolean);

    if (messages.length > 0) {
      return messages.join(', ');
    }
  }

  // Fallback to axios error message
  if (error?.message) {
    return error.message;
  }

  // Default fallback
  return 'An unexpected error occurred';
};

/**
 * Per-row rejections from a batch endpoint. The backend validates a whole batch
 * before writing anything and returns every bad row under `rows`, so a form can
 * mark the offending cells instead of showing one sentence for 200 rows.
 * @param error - The error object from the API call
 * @returns The rows array, or undefined when the response carries none
 */
export const getBackendErrorRows = <T>(error: any): T[] | undefined => {
  const rows = error?.response?.data?.rows;
  return Array.isArray(rows) ? (rows as T[]) : undefined;
};

/**
 * Helper function for API error handling that extracts backend message
 * @param error - The error object
 * @param fallbackMessage - Fallback message if no backend message found
 * @returns Backend error message or fallback
 */
export const extractApiErrorMessage = (
  error: any,
  fallbackMessage: string
): string => {
  const backendMessage = getBackendErrorMessage(error);

  // If the backend message is generic or empty, use the fallback
  if (
    backendMessage === 'An unexpected error occurred' ||
    backendMessage === 'Network Error' ||
    backendMessage.toLowerCase().includes('failed to fetch')
  ) {
    return fallbackMessage;
  }

  return backendMessage;
};
